import React, { useEffect, useState } from "react";
import { Ornament } from "@/components/Logo";
import { api } from "@/lib/api";
import { Award, Gem, Crown, Sparkles } from "lucide-react";

const ICONS = {
  collector: Award,
  benefactor: Gem,
  connoisseur: Crown,
  masters_circle: Sparkles,
};

function formatRange(min, max) {
  const f = (n) => {
    if (n >= 995_000)
      return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
    if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
    return `$${n}`;
  };
  return `${f(min)} — ${f(max)}`;
}

export default function Patronage() {
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    api
      .get("/patronage-tiers")
      .then((r) => setTiers(r.data || []))
      .catch(() => setTiers([]));
  }, []);

  if (!tiers.length) return null;

  return (
    <section
      id="patronage"
      data-testid="patronage-root"
      className="relative bg-[var(--ivory)] py-24 md:py-32 paper-texture"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-[10px] tracking-[0.5em] uppercase text-[#722F37]">
            Levels of Patronage
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight font-light">
            Every piece opens a door.
          </h2>
          <div className="mx-auto mt-6 w-40">
            <Ornament color="#D4AF37" />
          </div>
          <p className="mt-6 text-base text-[#1A1A1A]/70">
            Your patronage level is set by the price of the work you reserve.
            Every level is honoured with the gifts of every level beneath it,
            plus the additions listed below.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {tiers.map((t, i) => {
            const Icon = ICONS[t.key] || Award;
            const isTop = i === tiers.length - 1;
            return (
              <div
                key={t.key}
                data-testid={`patronage-tier-${t.key}`}
                className={`relative flex flex-col p-8 border transition-transform duration-500 hover:-translate-y-1 ${
                  isTop
                    ? "bg-[#0B3B24] text-ivory border-[#D4AF37]"
                    : "bg-white text-[#1A1A1A] border-[#D4AF37]/40"
                }`}
                style={isTop ? { color: "var(--ivory)" } : {}}
              >
                <div
                  className={`absolute top-6 right-6 ${
                    isTop ? "text-[#D4AF37]" : "text-[#722F37]"
                  }`}
                >
                  <Icon size={24} strokeWidth={1.4} />
                </div>

                <div
                  className={`text-[10px] tracking-[0.4em] uppercase ${
                    isTop ? "text-[#D4AF37]" : "text-[#722F37]"
                  }`}
                >
                  Level {i + 1}
                </div>
                <h3 className="font-serif text-3xl mt-2 tracking-tight">
                  {t.name}
                </h3>
                <div
                  className={`font-serif text-lg mt-1 ${
                    isTop ? "text-[#D4AF37]" : "text-[#722F37]"
                  }`}
                >
                  {formatRange(t.min, t.max)}
                </div>

                {t.tagline && (
                  <div
                    className={`font-serif italic mt-4 text-sm ${
                      isTop ? "text-ivory/80" : "text-[#1A1A1A]/60"
                    }`}
                  >
                    {t.tagline}
                  </div>
                )}

                <div
                  className={`mt-6 h-px w-16 ${
                    isTop ? "bg-[#D4AF37]/60" : "bg-[#D4AF37]/50"
                  }`}
                />

                <ul
                  data-testid={`patronage-gifts-${t.key}`}
                  className="mt-6 space-y-3 text-sm"
                >
                  {t.gifts.map((g, idx) => (
                    <li key={idx} className="flex gap-3">
                      <span
                        className={`mt-1 inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
                          isTop ? "bg-[#D4AF37]" : "bg-[#722F37]"
                        }`}
                      />
                      <span
                        className={`leading-snug ${
                          isTop ? "text-ivory/90" : "text-[#1A1A1A]/80"
                        }`}
                      >
                        {g}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center text-[10px] tracking-[0.35em] uppercase text-[#1A1A1A]/50">
          Payments secured by Escrow.com · funds held until you receive the work
        </div>

        {/* Portfolio showcase — the physical gift for upper-tier patrons */}
        <div
          data-testid="patronage-portfolio-showcase"
          className="mt-20 md:mt-28 relative bg-[#0B0F0C] text-ivory overflow-hidden"
          style={{ color: "var(--ivory)" }}
        >
          <div className="grid md:grid-cols-2 gap-0 items-stretch">
            <div className="relative">
              <img
                src="/master-brushes-portfolio.jpg"
                alt="House of Master Brushes bespoke leather portfolio — personally engraved with the collector's name, containing the Letter of Appreciation, Certificate of Authenticity, Representation Agreement, Provenance Record, and Artwork Registration."
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="p-10 md:p-16 flex flex-col justify-center relative">
              <div className="text-[10px] tracking-[0.5em] uppercase text-[#D4AF37]">
                For Connoisseur &amp; Master&apos;s Circle patrons
              </div>
              <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 tracking-tight font-light">
                The Master&apos;s Portfolio.
              </h3>
              <p className="mt-6 text-sm md:text-base text-ivory/80 leading-relaxed max-w-lg">
                Hand-bound in black leather. Foil-embossed with the House of
                Master Brushes crest on the cover — and, for Master&apos;s
                Circle patrons, personally engraved with the collector&apos;s
                name in gold. Interior gate-fold pages hold the full house
                dossier for your piece.
              </p>

              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-ivory/85">
                {[
                  "Letter of Appreciation",
                  "Certificate of Authenticity",
                  "Representation Agreement",
                  "Provenance Record",
                  "Artwork Registration",
                  "Archival care guide",
                ].map((k) => (
                  <li key={k} className="flex gap-3 items-start">
                    <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />
                    <span>{k}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10 pt-6 border-t border-[#D4AF37]/25 flex items-center gap-6 text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">
                <span>Excellence in art</span>
                <span className="w-6 h-px bg-[#D4AF37]/60" />
                <span>Legacy in collection</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
